import type Quill from 'quill/core';
import { Parchment } from 'quill/core';
import type { Range } from 'quill/core';
import { useEffect, useState } from 'react';
import type Mark from './Mark';

export const doesBlotManageRemoteSelections = (blot: Parchment.Blot) =>
  ['code-embed', 'table-embed'].includes(blot.statics.blotName);

const getEmbed = (
  quill: Quill,
  { length, index }: { length: number; index: number },
) => {
  if (length !== 1) return null;
  const [embed] = quill.scroll.descendant(Parchment.EmbedBlot, index);
  return embed;
};

export const useNestedTargetForRemoteSelection = (quill: Quill, mark: Mark) => {
  const [target, setTarget] = useState<NestedTargetForRemoteSelection | null>(
    null,
  );

  useEffect(() => {
    getNestedTargetForRemoteSelection(quill, mark).then(setTarget);
  }, [quill, mark]);

  return target;
};

type NestedTargetForRemoteSelection = {
  range: Range;
  quill: Quill;
  embed?: Parchment.EmbedBlot | null;
};

const getNestedTargetForRemoteSelection = async (
  quill: Quill,
  mark: Mark,
): Promise<NestedTargetForRemoteSelection | null> => {
  if (mark.type === 'plain') {
    const embed = getEmbed(quill, mark);
    if (embed && doesBlotManageRemoteSelections(embed)) {
      return null;
    }

    return { range: mark, quill, embed };
  }

  if (mark.type === 'cell') {
    const [leaf] = quill.getLeaf(mark.index);
    const cellQuill =
      leaf &&
      'getOrCreateCellQuill' in leaf &&
      typeof leaf.getOrCreateCellQuill === 'function'
        ? await leaf.getOrCreateCellQuill(mark.cell.identity)
        : null;
    if (cellQuill) {
      const embed = getEmbed(cellQuill, mark.cell);
      return { range: mark.cell, quill: cellQuill, embed };
    }
  }

  if (mark.type === 'code') {
    const [leaf] = quill.getLeaf(mark.index);
    const codeQuill =
      leaf && 'getQuill' in leaf && typeof leaf.getQuill === 'function'
        ? await leaf.getQuill()
        : null;
    if (codeQuill) {
      return { range: mark.code, quill: codeQuill };
    }
  }

  return null;
};

export default getNestedTargetForRemoteSelection;
